<template>
  <v-container fluid>
    <TopHeader>
      <template v-slot:title>
        <span class="text-h5">Merge duplicate products</span>
      </template>
    </TopHeader>

    <v-row>
      <v-col cols="12" xl="9">
        <!-- Pairs card -->
        <v-card outlined class="mb-5">
          <v-card-title class="text-subtitle-1 font-weight-bold py-3 px-5">
            Pairs to merge
            <v-spacer />
            <v-btn text small color="primary" @click="addPair">
              <v-icon left small>mdi-plus</v-icon>Add pair
            </v-btn>
          </v-card-title>
          <v-divider />
          <v-card-text class="pt-4">
            <v-row
              v-for="(pair, i) in pairs"
              :key="i"
              align="center"
              class="mb-1"
              no-gutters
            >
              <v-col cols="auto" class="pr-3">
                <span class="text-caption grey--text">#{{ i + 1 }}</span>
              </v-col>
              <v-col class="pr-3">
                <v-text-field
                  v-model.number="pair.doublon"
                  label="Doublon ID (to delete)"
                  placeholder="ex: 64619"
                  type="number"
                  min="1"
                  outlined dense hide-details clearable
                  :error="pair.doublon !== null && pair.doublon === pair.original"
                />
              </v-col>
              <v-col cols="auto" class="px-2">
                <v-icon color="grey lighten-1">mdi-arrow-right</v-icon>
              </v-col>
              <v-col class="pl-3">
                <v-text-field
                  v-model.number="pair.original"
                  label="Original ID (to keep)"
                  placeholder="ex: 66598"
                  type="number"
                  min="1"
                  outlined dense hide-details clearable
                  :error="pair.original !== null && pair.doublon === pair.original"
                />
              </v-col>
              <v-col cols="auto" class="pl-2">
                <v-btn icon small @click="removePair(i)" :disabled="pairs.length === 1">
                  <v-icon small>mdi-close</v-icon>
                </v-btn>
              </v-col>
            </v-row>
            <div v-if="hasSamePair" class="mt-3">
              <v-alert dense text type="warning" class="mb-0">
                Doublon and original must be different products.
              </v-alert>
            </div>
          </v-card-text>
        </v-card>

        <!-- Action buttons -->
        <div class="d-flex align-center">
          <v-btn
            outlined
            :disabled="!isValid || is.merging"
            :loading="is.dryRun"
            class="mr-3"
            @click="runDryRun"
          >
            <v-icon left>mdi-eye-outline</v-icon>
            Preview (dry run)
          </v-btn>
          <v-btn
            color="error"
            :disabled="!isValid || is.dryRun"
            :loading="is.merging"
            @click="directMergeDialog = true"
          >
            <v-icon left>mdi-merge</v-icon>
            Merge
          </v-btn>
        </div>

        <!-- Merge results -->
        <v-card v-if="mergeResults.length" outlined class="mt-5">
          <v-card-title class="text-subtitle-1 font-weight-bold py-3 px-5">
            <v-icon left color="success" small>mdi-check-circle-outline</v-icon>
            Merge results
          </v-card-title>
          <v-divider />
          <v-card-text>
            <div v-for="result in mergeResults" :key="result.doublon" class="mb-5">
              <div class="d-flex align-center flex-wrap mb-3" style="gap:8px">
                <span class="font-weight-medium">
                  {{ result.doublon_product ? `"${result.doublon_product.name}"` : '' }}
                  <span class="text-caption grey--text">(#{{ result.doublon }})</span>
                </span>
                <v-icon small>mdi-arrow-right</v-icon>
                <span class="font-weight-medium">
                  {{ result.original_product ? `"${result.original_product.name}"` : '' }}
                  <span class="text-caption grey--text">(#{{ result.original }})</span>
                </span>
                <v-chip small color="success" dark>Committed</v-chip>
              </div>
              <v-simple-table dense>
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left">Step</th>
                      <th class="text-left">Rows affected</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="step in result.steps" :key="step.label">
                      <td>{{ step.label }}</td>
                      <td>{{ step.rowcount }}</td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- ─── Dry run preview dialog ─────────────────────────────────────── -->
    <v-dialog v-model="previewDialog" max-width="800" scrollable>
      <v-card>
        <v-card-title class="text-h6 py-4 px-5">
          <v-icon left>mdi-eye-outline</v-icon>
          Preview — dry run
        </v-card-title>
        <v-divider />

        <v-card-text class="pa-0" style="max-height:600px">
          <v-expansion-panels v-model="previewExpanded" multiple flat tile>
            <v-expansion-panel
              v-for="(result, ri) in previewResults"
              :key="result.doublon"
            >
              <!-- Panel header: product names -->
              <v-expansion-panel-header class="px-5 py-3">
                <div class="d-flex align-center flex-wrap" style="gap:6px;min-width:0">
                  <template v-if="result.doublon_product && result.original_product">
                    <span class="font-weight-medium text-truncate">
                      "{{ result.doublon_product.name }}"
                      <span class="text-caption grey--text ml-1">#{{ result.doublon }}</span>
                    </span>
                    <v-icon small color="grey darken-1">mdi-arrow-right</v-icon>
                    <span class="text-caption grey--text text--darken-1">merge into</span>
                    <v-icon small color="grey darken-1">mdi-arrow-right</v-icon>
                    <span class="font-weight-medium text-truncate">
                      "{{ result.original_product.name }}"
                      <span class="text-caption grey--text ml-1">#{{ result.original }}</span>
                    </span>
                  </template>
                  <template v-else>
                    <span class="grey--text">
                      Doublon #{{ result.doublon }} → Original #{{ result.original }}
                    </span>
                    <v-icon small color="error" class="ml-1">mdi-alert-circle</v-icon>
                  </template>
                </div>
              </v-expansion-panel-header>

              <v-expansion-panel-content eager>
                <div class="px-1 pt-1 pb-3">

                  <!-- Blocking errors: product not found -->
                  <v-alert v-if="!result.doublon_product" dense text type="error" class="mb-2">
                    Doublon #{{ result.doublon }} not found — cannot proceed with this pair.
                  </v-alert>
                  <v-alert v-if="!result.original_product" dense text type="error" class="mb-2">
                    Original #{{ result.original }} not found — cannot proceed with this pair.
                  </v-alert>

                  <!-- Zero-impact warning -->
                  <v-alert
                    v-if="result.doublon_product && result.original_product && isAllZero(result)"
                    dense text type="warning" class="mb-2"
                  >
                    Nothing will be modified — are you sure?
                  </v-alert>

                  <!-- Impact table -->
                  <v-simple-table dense>
                    <template v-slot:default>
                      <thead>
                        <tr>
                          <th class="text-left">Section</th>
                          <th class="text-left" style="width:72px">Count</th>
                          <th class="text-left">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="section in IMPACT_SECTIONS"
                          :key="section.key"
                          :class="getSectionCount(result, section.key) === 0 && !getSectionError(result, section.key) ? 'grey--text text--lighten-1' : ''"
                        >
                          <td>
                            {{ section.label }}
                            <v-tooltip
                              v-if="getSectionError(result, section.key)"
                              bottom max-width="320"
                            >
                              <template v-slot:activator="{ on }">
                                <v-icon small color="warning" class="ml-1" v-on="on">
                                  mdi-alert
                                </v-icon>
                              </template>
                              <span>{{ getSectionError(result, section.key) }}</span>
                            </v-tooltip>
                          </td>
                          <td>
                            <v-chip
                              x-small
                              :color="getSectionCount(result, section.key) > 0 ? 'primary' : 'grey lighten-2'"
                              :dark="getSectionCount(result, section.key) > 0"
                            >
                              {{ getSectionCount(result, section.key) }}
                            </v-chip>
                          </td>
                          <td class="text-caption">
                            <span v-if="getSectionError(result, section.key)" class="warning--text">
                              Could not check (table unreachable)
                            </span>
                            <span
                              v-else-if="getSectionCount(result, section.key) === 0"
                              class="grey--text text--lighten-1"
                            >
                              Nothing to change
                            </span>
                            <span v-else>{{ section.action }}</span>
                          </td>
                        </tr>
                      </tbody>
                    </template>
                  </v-simple-table>

                </div>
              </v-expansion-panel-content>

              <v-divider v-if="ri < previewResults.length - 1" />
            </v-expansion-panel>
          </v-expansion-panels>
        </v-card-text>

        <v-divider />
        <v-card-actions class="px-5 py-3">
          <v-spacer />
          <v-btn text @click="previewDialog = false">Cancel</v-btn>
          <v-btn
            color="error"
            :disabled="!previewCanConfirm"
            :loading="is.merging"
            @click="confirmMerge"
          >
            <v-icon left>mdi-merge</v-icon>
            Confirm merge
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Direct merge confirmation dialog -->
    <v-dialog v-model="directMergeDialog" max-width="440">
      <v-card>
        <v-card-title class="text-h6">Confirm merge</v-card-title>
        <v-card-text>
          This will permanently delete
          <strong>{{ pairs.length }} product(s)</strong> and reassign all
          their relationships. This action cannot be undone.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="directMergeDialog = false">Cancel</v-btn>
          <v-btn color="error" @click="confirmMerge" :loading="is.merging">
            Confirm merge
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import TopHeader from "@/components/base/TopHeader.vue"
import { MergeDuplicatesService } from "@/services"

const IMPACT_SECTIONS = [
  { key: "favorites",           label: "Favorites",           action: "Reassigned to original" },
  { key: "ratings",             label: "Ratings",             action: "Reassigned to original" },
  { key: "olfactive_universes", label: "Olfactive universes", action: "Deleted" },
  { key: "notes",               label: "Notes",               action: "Deleted" },
  { key: "translations",        label: "Translations",        action: "Deleted" },
  { key: "product",             label: "Product", action: "Deleted" },
]

const newPair = () => ({ doublon: null, original: null })

export default {
  name: "MergeDuplicates",
  components: { TopHeader },

  data: () => ({
    pairs: [newPair()],
    is: { dryRun: false, merging: false },
    previewDialog: false,
    previewResults: [],
    previewExpanded: [],
    directMergeDialog: false,
    mergeResults: []
  }),

  computed: {
    IMPACT_SECTIONS: () => IMPACT_SECTIONS,

    hasSamePair() {
      return this.pairs.some(p => p.doublon !== null && p.doublon === p.original)
    },

    isValid() {
      return (
        this.pairs.length > 0 &&
        this.pairs.every(
          p => p.doublon > 0 && p.original > 0 && p.doublon !== p.original
        )
      )
    },

    previewCanConfirm() {
      return (
        this.previewResults.length > 0 &&
        this.previewResults.every(
          r => r.doublon_product !== null && r.original_product !== null
        )
      )
    }
  },

  methods: {
    addPair() {
      this.pairs.push(newPair())
    },

    removePair(i) {
      this.pairs.splice(i, 1)
    },

    getSectionCount(result, key) {
      return result.impact?.[key]?.count ?? 0
    },

    getSectionError(result, key) {
      return result.impact?.[key]?.error ?? null
    },

    isAllZero(result) {
      if (!result.impact) return true
      return Object.values(result.impact).every(s => (s.count ?? 0) === 0)
    },

    async runDryRun() {
      this.is.dryRun = true
      try {
        const payload = this.pairs.map(p => ({
          doublon: p.doublon,
          original: p.original
        }))
        const data = await MergeDuplicatesService.mergeDuplicates(this, payload, true)
        this.previewResults = data.results
        this.previewExpanded = data.results.map((_, i) => i)
        this.previewDialog = true
      } finally {
        this.is.dryRun = false
      }
    },

    async confirmMerge() {
      this.is.merging = true
      try {
        const payload = this.pairs.map(p => ({
          doublon: p.doublon,
          original: p.original
        }))
        const data = await MergeDuplicatesService.mergeDuplicates(this, payload, false)
        this.mergeResults = data.results
        this.previewDialog = false
        this.directMergeDialog = false
        this.$store.commit("success", "Products merged successfully!")
        this.pairs = [newPair()]
      } catch (_) {
        // error already dispatched to store by service
      } finally {
        this.is.merging = false
      }
    }
  }
}
</script>

